import classNames from "classnames";
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { LottieLoading } from "../../components/graphics/LottieLoading";
import {
  useGetHouseBackfillQuery,
  useGetNewFirmsQuery,
  useStartHouseBackfillMutation,
} from "../../slices/SupportSlice";
import { FirmWorkStatusSelect, HelpDeskHeader } from "./HelpDeskParts";
import {
  errorText,
  firmEmailLine,
  firmSetupLine,
  PILL_CLASSES,
  STEP_LABELS,
} from "./support.utils";
import { PAGE, PAGE_INNER, PRIMARY_BUTTON, SMALL_BUTTON } from "./supportUi";

/**
 * Help desk › New firms (owner 2026-10-25): firms that signed up since this went live. Their welcome / check-in /
 * trial-ending emails go out by themselves; a reply becomes a case and stops the rest. Each firm is also a client
 * (with an "Onboarding – …" work item) in the owner's own firm.
 */
export const NewFirmsPage = () => {
  const { data, isLoading, isError } = useGetNewFirmsQuery(undefined, {
    pollingInterval: 300000,
  });
  const firms = data?.firms || [];
  return (
    <div className={PAGE}>
      <div className={PAGE_INNER}>
        <HelpDeskHeader
          section={"firms"}
          subtitle={
            "Firms that signed up for the free trial. Welcome, check-in and trial-ending emails are prepared for you as drafts in Support cases: you check them and click Send. A reply from the firm becomes a case and stops the later emails."
          }
        />
        <HouseLine name={data?.houseFirmName} problem={data?.houseProblem} />
        <div
          className={
            "overflow-hidden rounded-[14px] border border-[#EDEFF3] bg-white"
          }
        >
          <div
            className={
              "hidden grid-cols-[1.5fr_150px_120px_2fr_2.1fr_120px] gap-3.5 px-[18px] py-2.5 text-[12px] font-bold uppercase tracking-[.5px] text-[#5D6475] lg:grid"
            }
          >
            <span>Firm</span>
            <span>Status</span>
            <span>Trial</span>
            <span>Getting started</span>
            <span>Emails</span>
            <span>Reply</span>
          </div>
          {isError ? (
            <div className={"px-4 py-10 text-center text-sm text-[#8A90A2]"}>
              The new firms couldn&apos;t be loaded. Please refresh the page.
            </div>
          ) : isLoading ? (
            <div className={"py-8"}>
              <LottieLoading />
            </div>
          ) : firms.length === 0 ? (
            <div className={"px-4 py-12 text-center"}>
              <div className={"text-[15px] font-semibold text-[#1D2C3B]"}>
                No new firms yet
              </div>
              <div className={"mt-1 text-sm text-[#8A90A2]"}>
                Firms show here as soon as they sign up for the free trial.
              </div>
            </div>
          ) : (
            firms.map((f) => {
              const setup = firmSetupLine(f.setup, f.startedAt);
              const late =
                !setup && !f.steps.clients && f.trialDay >= 3 && !f.paying;
              const pct = Math.min(
                100,
                Math.round((Math.min(f.trialDay, 14) / 14) * 100),
              );
              return (
                <div
                  key={f.orgId}
                  className={classNames(
                    "grid grid-cols-1 gap-3 border-t border-[#EDEFF3] px-[18px] py-4 lg:grid-cols-[1.5fr_150px_120px_2fr_2.1fr_120px] lg:items-center lg:gap-3.5",
                    late && "bg-[#FFF9EE]",
                  )}
                >
                  <div className={"min-w-0"}>
                    <div
                      className={
                        "truncate text-[14.5px] font-bold text-[#1F2433]"
                      }
                    >
                      {f.firmName}
                    </div>
                    <div className={"truncate text-[12.5px] text-[#5D6475]"}>
                      {f.ownerName || f.ownerEmail} · {f.people}{" "}
                      {f.people === 1 ? "person" : "people"}
                    </div>
                    <div
                      className={"mt-0.5 flex flex-wrap gap-2 text-[12.5px]"}
                    >
                      {f.clientLink && (
                        <Link
                          to={f.clientLink}
                          className={"font-semibold text-[#6B4EFF]"}
                        >
                          Client ›
                        </Link>
                      )}
                      {f.workLink && (
                        <Link
                          to={f.workLink}
                          className={"font-semibold text-[#6B4EFF]"}
                        >
                          Onboarding work ›
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className={"min-w-0"}>
                    <FirmWorkStatusSelect
                      orgId={f.orgId}
                      status={f.workStatus}
                      choices={
                        (f.workStatus?.workTypeId &&
                          data?.workStatusChoices?.[f.workStatus.workTypeId]) ||
                        []
                      }
                    />
                  </div>
                  <div>
                    {setup ? (
                      <span
                        className={classNames(
                          "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                          PILL_CLASSES.amber,
                        )}
                      >
                        Not set up
                      </span>
                    ) : f.paying ? (
                      <span
                        className={classNames(
                          "rounded-full px-2.5 py-0.5 text-[12px] font-bold",
                          PILL_CLASSES.green,
                        )}
                      >
                        Customer
                      </span>
                    ) : (
                      <>
                        <div className={"text-[14px] font-bold text-[#1F2433]"}>
                          Day {Math.min(f.trialDay, 99)} of 14
                        </div>
                        <div
                          className={
                            "mt-1.5 h-1.5 overflow-hidden rounded-full bg-[#EDEFF3]"
                          }
                        >
                          <div
                            className={"h-1.5 bg-[#6B4EFF]"}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </>
                    )}
                  </div>
                  <div className={"flex flex-wrap gap-1.5"}>
                    {setup && (
                      <span
                        className={classNames(
                          "rounded-full px-2.5 py-0.5 text-[12px] font-semibold",
                          PILL_CLASSES.amber,
                        )}
                      >
                        {setup.text}
                      </span>
                    )}
                    {!setup &&
                      STEP_LABELS.map((step) => {
                        const done = f.steps[step.key];
                        return (
                          <span
                            key={step.key}
                            className={classNames(
                              "rounded-full px-2.5 py-0.5 text-[12px] font-semibold",
                              done ? PILL_CLASSES.green : PILL_CLASSES.grey,
                            )}
                          >
                            {done ? "✓ " : "○ "}
                            {step.label}
                          </span>
                        );
                      })}
                  </div>
                  <div className={"flex flex-wrap gap-1.5"}>
                    {setup && (
                      <div className={"w-full text-[12px] text-[#8A90A2]"}>
                        {setup.note}
                      </div>
                    )}
                    {!setup &&
                      f.emails.map((e) => {
                        const line = firmEmailLine(e);
                        const cls = classNames(
                          "rounded-md px-2 py-0.5 text-[12px]",
                          PILL_CLASSES[line.tone],
                        );
                        // A draft (or a sent email) opens its case: the owner reviews every email.
                        return e.ticketId ? (
                          <Link
                            key={e.key}
                            to={`/support/${e.ticketId}`}
                            className={classNames(
                              cls,
                              "hover:underline",
                              e.state === "draft" && "font-bold",
                            )}
                          >
                            {line.text}
                          </Link>
                        ) : (
                          <span key={e.key} className={cls}>
                            {line.text}
                          </span>
                        );
                      })}
                    {f.lastEmailError && (
                      <div className={"w-full text-[12px] text-[#B42318]"}>
                        {f.lastEmailError}
                      </div>
                    )}
                    {f.houseError && (
                      <div className={"w-full text-[12px] text-[#9A5B00]"}>
                        Client not added: {f.houseError}
                      </div>
                    )}
                  </div>
                  <div className={"text-[13px] font-semibold"}>
                    {f.ticketId ? (
                      <Link
                        to={`/support/${f.ticketId}`}
                        className={"text-[#1F5BB8]"}
                      >
                        Wrote to us ›
                      </Link>
                    ) : (
                      <span className={"text-[#5D6475]"}>No reply</span>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div
          className={
            "mt-4 rounded-[14px] border border-[#EDEFF3] bg-white px-[18px] py-4 text-[13.5px] leading-relaxed text-[#3A4256]"
          }
        >
          <b className={"text-[#1F2433]"}>Smart check-ins:</b> the Day 3 email
          gives the tip for the first step a firm hasn&apos;t done yet (add
          clients, then create work, then invite the team). The trial-ending
          email is skipped when they already added a card. You can change every
          text in{" "}
          <Link
            to={"/support/automatic-emails"}
            className={"font-semibold text-[#6B4EFF]"}
          >
            Automatic emails
          </Link>
          .
        </div>
        <Backfill />
      </div>
    </div>
  );
};

const HouseLine = ({
  name,
  problem,
}: {
  name?: string | null;
  problem?: string | null;
}) =>
  problem ? (
    <div
      className={
        "mb-4 rounded-[14px] border border-[#F5DEB0] bg-[#FFF9EE] px-4 py-3 text-[13.5px] text-[#7A5A12]"
      }
    >
      <b>New firms aren&apos;t added to your clients:</b> {problem}
    </div>
  ) : name ? (
    <div className={"mb-4 text-[13.5px] text-[#5D6475]"}>
      Each new firm is added as a client in{" "}
      <b className={"text-[#1F2433]"}>{name}</b> (tag &ldquo;Free trial&rdquo;,
      then &ldquo;Customer&rdquo; when they add a card), with an
      &ldquo;Onboarding – firm&rdquo; work item due when the trial ends,
      assigned to you.
    </div>
  ) : null;

/** "Add the firms that signed up before" (owner 2026-10-25): once, as clients in the owner's firm. */
const Backfill = () => {
  const { data } = useGetHouseBackfillQuery();
  const [start, { isLoading }] = useStartHouseBackfillMutation();
  const [asked, setAsked] = useState(false);
  if (!data || data.houseProblem) return null;
  return (
    <div
      className={
        "mt-4 rounded-[14px] border border-[#EDEFF3] bg-white px-[18px] py-4 text-[13.5px] text-[#3A4256]"
      }
    >
      <div className={"mb-1 text-[15px] font-extrabold text-[#1F2433]"}>
        Firms that signed up before
      </div>
      {data.started ? (
        <div>
          Adding them now in the background. Refresh this page in a few minutes.
        </div>
      ) : data.candidates === 0 ? (
        <div>
          All earlier firms are clients in {data.houseFirmName}
          {data.added ? ` (${data.added} added)` : ""}.
        </div>
      ) : (
        <>
          <div>
            {data.candidates} firm{data.candidates === 1 ? "" : "s"} signed up
            before and {data.candidates === 1 ? "isn't" : "aren't"} a client in{" "}
            {data.houseFirmName} yet
            {data.sample.length > 0
              ? ` (for example ${data.sample.slice(0, 5).join(", ")})`
              : ""}
            . They get the tag &ldquo;Customer&rdquo; (or &ldquo;Free
            trial&rdquo; with a work item if they are still in a trial). No
            emails are sent to them.
          </div>
          <div className={"mt-3 flex flex-wrap gap-2"}>
            {asked ? (
              <>
                <button
                  type={"button"}
                  disabled={isLoading}
                  className={PRIMARY_BUTTON}
                  onClick={() =>
                    start()
                      .unwrap()
                      .then(() =>
                        toast("Adding the earlier firms as clients…", {
                          type: "success",
                        }),
                      )
                      .catch((e) =>
                        toast(
                          errorText(e, "Couldn't start it. Please try again."),
                          { type: "error" },
                        ),
                      )
                  }
                >
                  {isLoading
                    ? "Starting…"
                    : `Yes, add ${data.candidates} client${
                        data.candidates === 1 ? "" : "s"
                      }`}
                </button>
                <button
                  type={"button"}
                  className={SMALL_BUTTON}
                  onClick={() => setAsked(false)}
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                type={"button"}
                className={SMALL_BUTTON}
                onClick={() => setAsked(true)}
              >
                Add them to my clients…
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};
